import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import useSearchClient from "../../utils/hooks/useSearchClient"
import { InstantSearch, Configure, Index } from "react-instantsearch"
import CustomHits from "./CustomHits"
import CustomSearchBox from "./CustomSearchBox"
import SearchReset from "./SearchReset"
import CustomRefinementList from "./CustomRefinementList"
import { useQueryParam, StringParam } from "use-query-params"
import { debounce } from "lodash"
import RecentAndCommonSearches from "./RecentAndCommonSearches"
import { storeSearchLocally } from "./NavSearchMethods"
import "./NavSearch.scss"
import CustomStats from "./CustomStats"
import InsightsMiddleware from "./InsightsMiddleware"
import LanguageDropdown from "./LanguageDropdown"

const NavSearch = ({
  data,
  initialParams,
  searchDeployed,
  commonSearches,
  locale,
  globalSearch,
  navigationData,
  searchPromoCardsData,
  languageSettings,
}) => {
  const crawlerIndex = globalSearch
    ? process.env.GATSBY_ALGOLIA_GLOBAL_INDEX
    : process.env.GATSBY_ALGOLIA_SITE_INDEX || "crawler_external_sites"

  const hitsPerPage = process.env.GATSBY_ALGOLIA_SEARCH_HITS_PER_PAGE || 10
  const hitsPerIndex = hitsPerPage / 2

  const disableLanguageRegion = navigationData?.disableLanguageRegion
  const languages = navigationData?.languageSelectorData?.languages

  let processedLocale = locale

  if (locale.toLowerCase() === "en") {
    processedLocale = "en-US"
  }

  const localeIndexFilter = `locale:${processedLocale} OR locale:${processedLocale.toLowerCase()} OR locale:${
    processedLocale.split("-")[0]
  } OR locale:${processedLocale.toLowerCase().split("-")[0]}`

  const {
    searchResultsLabelText = data?.searchResultsLabelText,
    searchSearchPlaceholderText = data?.searchSearchPlaceholderText,
    searchResetButtonText = data?.searchResetButtonText,
    searchRecentSearchesText = data?.searchRecentSearchesText,
    searchCommonSearchesText = data?.searchCommonSeachesText,
    globalSearchLabel = data?.glocalSearchLabel,
  } = data || {}

  const { searchClient, combinedNbHits, results, currentPageNumber } =
    useSearchClient()

  const [currentRefinement, setCurrentRefinement] = useState(
    initialParams || ""
  )

  const searchWrapperRef = useRef(null)

  //we aren't using searchQuery here to avoid an infinite loop
  //since we want to update the query param as the user types
  /* eslint-disable-next-line no-unused-vars */
  const [_searchQuery, setSearchQuery] = useQueryParam("search", StringParam)

  const [currentLanguage, setCurrentLanguage] = useState(
    languageSettings?.language?.nativeField
  )
  const [currentLocale, setCurrentLocale] = useState(
    languageSettings?.language?.code
  )
  const [isOpen, setIsOpen] = useState(false)

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const clearSearch = useCallback(() => {
    //If we have a currentRefinement (search string), we want to update the searchQuery
    //if currentRefinement is empty string, we want to set searchQuery to undefined
    //this removes the query from the URL entirely
    //e.g. currentRefinement = "" -> url/?search=
    //currentRefinement = undefined -> url/
    setSearchQuery(undefined, "replaceIn")
    setCurrentRefinement("")
  }, [setCurrentRefinement, setSearchQuery])

  const resetClick = () => {
    clearSearch()
  }

  const handleSearchChange = searchText => {
    if (searchText?.length >= 2) {
      setSearchQuery(searchText, "replaceIn")
      storeSearchLocally(searchText, commonSearches?.length)
    } else {
      setSearchQuery(undefined, "replaceIn")
    }
  }

  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  const debounceQuery = useMemo(() => debounce(handleSearchChange, 300), [])

  useEffect(() => {
    debounceQuery(currentRefinement)
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentRefinement])

  useEffect(() => {
    if (!searchDeployed) {
      clearSearch()
    }
  }, [searchDeployed, clearSearch])

  return !!searchClient ? (
    <div className="nav-search" ref={searchWrapperRef}>
      {globalSearch && (
        <p className="nav-search__global-text">{globalSearchLabel}</p>
      )}
      <InstantSearch
        indexName={crawlerIndex}
        searchClient={searchClient}
        initialUiState={{
          "geospatial-production": {
            refinementList: {
              locale: [`${languageSettings?.language?.code}`],
            },
          },
          "www-sketchup-production": {
            refinementList: {
              locale: [`${languageSettings?.language?.code}`],
            },
          },
        }}
      >
        <Configure clickAnalytics />
        <CustomSearchBox
          searchText={currentRefinement}
          setSearchText={setCurrentRefinement}
          searchDeployed={searchDeployed}
          searchPlaceholderText={searchSearchPlaceholderText}
        />
        <div className="luna-nav-search__filter-panel">
          <CustomRefinementList attribute="contentType" />
          {!disableLanguageRegion &&
            languages?.length > 1 &&
            currentRefinement?.length >= 2 && (
              <LanguageDropdown
                languages={languages}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                handleToggle={handleToggle}
                currentLanguage={currentLanguage}
                setCurrentLocale={setCurrentLocale}
                setCurrentLanguage={setCurrentLanguage}
              />
            )}
          <Configure
            filters={`locale:${currentLocale}`}
            attributesToRetrieve={["*"]}
            hitsPerPage={hitsPerIndex}
            clickAnalytics
            facets={["*"]}
          />
        </div>
        {currentRefinement?.length >= 2 ? (
          <>
            <SearchReset
              resetClick={resetClick}
              resetSearchText={searchResetButtonText}
            />
            <CustomStats currentPageNumber={currentPageNumber} />
            <Index indexName={crawlerIndex}>
              <Configure
                hitsPerPage={hitsPerIndex}
                filters={`locale:${currentLocale}`}
                clickAnalytics
              />
              <CustomHits
                minHitsPerPage={hitsPerIndex}
                totalHits={combinedNbHits}
                searchWrapperRef={searchWrapperRef}
                locale={locale}
                bannerComponent
                searchPromoCardsData={searchPromoCardsData}
              />
            </Index>
          </>
        ) : (
          <RecentAndCommonSearches
            commonSearches={commonSearches}
            recentSearchesText={searchRecentSearchesText}
            commonSearchesText={searchCommonSearchesText}
            setCurrentRefinement={setCurrentRefinement}
          />
        )}
        <InsightsMiddleware />
      </InstantSearch>
    </div>
  ) : null
}

export default React.memo(NavSearch)
